import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    Container, 
    CircularProgress,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    IconButton,
    Popover,
    Chip,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    useTheme
} from '@mui/material';
import { Global } from '@emotion/react';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import { sandboxConsentsService, SandboxConsentData, SandboxConsentFilters } from '../../services/sandbox-consents-service';
import { languageService } from '../../services/language-service';
import { useNavigate } from 'react-router-dom';

const SandboxConsents: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    
    // State management
    const [consents, setConsents] = useState<SandboxConsentData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // Pagination state
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);
    
    // Filter state (excluding sort - sort is managed separately)
    const [filters, setFilters] = useState<Omit<SandboxConsentFilters, 'sortBy'>>({
        page: 1,
        pageSize: 20
    });

    // Sort state (completely separate from filters)
    const [sortBy, setSortBy] = useState<string>('newest');

    // Filter UI state (temp filters for the popup, excluding sort)
    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
    const [tempFilters, setTempFilters] = useState<Omit<SandboxConsentFilters, 'sortBy'>>({
        page: 1,
        pageSize: 20
    });
    const [filterLoading, setFilterLoading] = useState(false);

    // Collection points for filter dropdown
    const [collectionPoints, setCollectionPoints] = useState<Array<{CollectionPointId: number, Name: string}>>([]);

    // Supported languages for tooltips
    const [languages, setLanguages] = useState<Array<{code: string, name: string, localeName: string}>>([]);

    // Data fetching effect
    useEffect(() => {
        loadSandboxConsents();
    }, [page, pageSize, filters.collectionPointId, filters.startDate, filters.endDate, filters.search, sortBy]);

    // Load collection points for filter dropdown
    useEffect(() => {
        loadCollectionPoints();
    }, []);

    // Load supported languages for tooltips
    useEffect(() => {
        loadLanguages();
    }, []);

    const loadCollectionPoints = async () => {
        try {
            // This would typically call a collection points service
            // For now, we'll use mock data or you can implement the actual service call
            const mockCollectionPoints = [
                { CollectionPointId: 39, Name: "Site Visit CP" },
                { CollectionPointId: 40, Name: "Contact Form CP" },
                { CollectionPointId: 41, Name: "Newsletter Signup CP" }
            ];
            setCollectionPoints(mockCollectionPoints);
        } catch (error) {
            console.error('Error loading collection points:', error);
        }
    };

    const loadLanguages = async () => {
        try {
            console.log('Loading languages...');
            const response = await languageService.getSupportedLanguages();
            console.log('Languages loaded:', response);
            setLanguages(response);
        } catch (error) {
            console.error('Error loading languages:', error);
        }
    };

    const getLanguageTooltip = (languageCode: string): string => {
        const language = languages.find(lang => 
            lang.code.toLowerCase() === languageCode.toLowerCase()
        );
        
        if (language) {
            // Show both English name and native name if different
            if (language.name !== language.localeName) {
                return `${language.name} (${language.localeName})`;
            }
            return language.name;
        }
        
        // Handle common missing languages with fallback names
        const fallbackNames: { [key: string]: string } = {
            'en': 'English',
            'es': 'Spanish',
            'fr': 'French',
            'de': 'German',
            'it': 'Italian',
            'pt': 'Portuguese',
            'ru': 'Russian',
            'ja': 'Japanese',
            'ko': 'Korean',
            'zh': 'Chinese'
        };
        
        const fallbackName = fallbackNames[languageCode.toLowerCase()];
        if (fallbackName) {
            return fallbackName;
        }
        
        return languageCode.toUpperCase();
    };

    const loadSandboxConsents = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const queryParams = {
                filters: {
                    collectionPointId: filters.collectionPointId,
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                    search: filters.search
                },
                pagination: {
                    page: page,
                    pageSize: pageSize
                },
                sortBy: sortBy
            };
            
            const response = await sandboxConsentsService.getSandboxConsents(queryParams);
            
            if (response.success) {
                setConsents(response.data);
                setTotalRecords(response.pagination.totalRecords);
                setTotalPages(response.pagination.totalPages);
                setHasNextPage(response.pagination.hasNextPage);
                setHasPrevPage(response.pagination.hasPrevPage);
            } else {
                setError('Failed to fetch sandbox consents');
            }
        } catch (error) {
            console.error('Error loading sandbox consents:', error);
            setError(error instanceof Error ? error.message : 'Failed to fetch sandbox consents');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(1); // Reset to first page when changing page size
    };

    const handleFilterChange = (newFilters: Partial<SandboxConsentFilters>) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
        setPage(1); // Reset to first page when applying filters
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            page: 1,
            pageSize: 20
        };
        // Reset both filters and sort to their default states
        setFilters(clearedFilters);
        setTempFilters(clearedFilters);
        setPage(1);
        setSortBy('newest'); // Reset sort to default
    };

    // Filter UI handlers
    const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setFilterAnchorEl(event.currentTarget);
        setTempFilters({ ...filters }); // Reset temp filters to current applied filters (sort is separate)
    };

    const handleFilterClose = () => {
        setFilterAnchorEl(null);
    };

    const handleTempFilterChange = (field: keyof SandboxConsentFilters, value: any) => {
        setTempFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleApplyFilters = async () => {
        try {
            setFilterLoading(true);
            setFilters(tempFilters); // Apply the current temp filter values
            setPage(1); // Reset to first page when applying filters
            handleFilterClose();
        } catch (error) {
            console.error('Error applying filters:', error);
        } finally {
            setFilterLoading(false);
        }
    };

    const hasActiveFilters = () => {
        return filters.collectionPointId || filters.startDate || filters.endDate || filters.search;
    };

    const hasUnsavedFilterChanges = () => {
        return tempFilters.collectionPointId !== filters.collectionPointId ||
               tempFilters.startDate !== filters.startDate ||
               tempFilters.endDate !== filters.endDate ||
               tempFilters.search !== filters.search;
    };

    const handleConsentClick = (consentId: number) => {
        navigate(`/sandbox/consents/${consentId}`);
    };

    if (loading && consents.length === 0) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error && consents.length === 0) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ py: 3 }}>
                    <Typography variant="h6" color="error" gutterBottom>
                        Error Loading Sandbox Consents
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        {error}
                    </Typography>
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            There was an error loading the sandbox consent records. Please try refreshing the page.
                        </Typography>
                    </Paper>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ py: 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <AssignmentIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'text.primary' }}>
                            Sandbox Consent Records
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            View and manage consent records from sandbox environment
                        </Typography>
                    </Box>
                </Box>

                {/* Controls */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    mb: 3
                }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* Left side controls can be added here if needed */}
                    </Box>
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        ml: 'auto'
                    }}>
                        <IconButton
                            onClick={loadSandboxConsents}
                            sx={{ color: theme.palette.text.primary }}
                            disabled={loading}
                        >
                            <RefreshIcon
                                sx={{
                                    transition: 'transform 0.3s',
                                    animation: loading ? 'spin 1s linear infinite' : undefined
                                }}
                            />
                        </IconButton>
                        <Select
                            value={sortBy}
                            onChange={(e) => {
                                setSortBy(e.target.value as string);
                            }}
                            size="small"
                            sx={{ 
                                width: 150,
                                bgcolor: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.divider,
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main,
                                },
                                '& .MuiSelect-icon': {
                                    color: theme.palette.text.primary,
                                }
                            }}
                        >
                            <MenuItem value="A-Z" sx={{ color: theme.palette.text.primary }}>A - Z</MenuItem>
                            <MenuItem value="Z-A" sx={{ color: theme.palette.text.primary }}>Z - A</MenuItem>
                            <MenuItem value="newest" sx={{ color: theme.palette.text.primary }}>Newest</MenuItem>
                            <MenuItem value="oldest" sx={{ color: theme.palette.text.primary }}>Oldest</MenuItem>
                        </Select>
                        <IconButton 
                            onClick={handleFilterClick}
                            sx={{ 
                                color: (hasActiveFilters() || hasUnsavedFilterChanges()) ? theme.palette.primary.main : theme.palette.text.primary,
                                '&:hover': {
                                    bgcolor: theme.palette.action.hover,
                                },
                                position: 'relative',
                                '&::after': (hasActiveFilters() || hasUnsavedFilterChanges()) ? {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: theme.palette.primary.main,
                                    border: `2px solid ${theme.palette.background.paper}`,
                                } : {}
                            }}
                        >
                            <FilterListIcon />
                        </IconButton>
                        {(hasActiveFilters() || sortBy !== 'newest') && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleClearFilters}
                                sx={{ 
                                    color: 'primary.main',
                                    borderColor: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        borderColor: 'primary.dark',
                                    }
                                }}
                            >
                                Clear All
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* CSS Animation for Refresh Icon */}
                <Global styles={`
                    @keyframes spin {
                        100% { transform: rotate(360deg); }
                    }
                `} />

                {/* Filter Popover */}
                <Popover
                    open={Boolean(filterAnchorEl)}
                    anchorEl={filterAnchorEl}
                    onClose={handleFilterClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            mt: 1,
                            width: 400,
                            bgcolor: 'background.paper',
                            boxShadow: theme.shadows[8],
                        },
                    }}
                >
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Filters
                        </Typography>
                        
                        {/* Collection Point Filter */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                                Collection Point
                            </Typography>
                            <FormControl fullWidth size="small">
                                <InputLabel>All Collection Points</InputLabel>
                                <Select
                                    value={tempFilters.collectionPointId || ''}
                                    onChange={(e) => handleTempFilterChange('collectionPointId', e.target.value || undefined)}
                                    label="All Collection Points"
                                >
                                    <MenuItem value="">All Collection Points</MenuItem>
                                    {collectionPoints.map((cp) => (
                                        <MenuItem key={cp.CollectionPointId} value={cp.CollectionPointId}>
                                            {cp.Name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Date Range Filters */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                                Date Range
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label="Start Date"
                                    type="date"
                                    value={tempFilters.startDate ? new Date(tempFilters.startDate).toISOString().split('T')[0] : ''}
                                    onChange={(e) => handleTempFilterChange('startDate', e.target.value ? new Date(e.target.value) : undefined)}
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    label="End Date"
                                    type="date"
                                    value={tempFilters.endDate ? new Date(tempFilters.endDate).toISOString().split('T')[0] : ''}
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            // Set end date to end of day (23:59:59.999)
                                            const endDate = new Date(e.target.value);
                                            endDate.setHours(23, 59, 59, 999);
                                            handleTempFilterChange('endDate', endDate);
                                        } else {
                                            handleTempFilterChange('endDate', undefined);
                                        }
                                    }}
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>
                        </Box>

                        {/* Search Filter */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                                Search Identifier
                            </Typography>
                            <TextField
                                label="Search by email or phone"
                                value={tempFilters.search || ''}
                                onChange={(e) => handleTempFilterChange('search', e.target.value || undefined)}
                                size="small"
                                fullWidth
                                placeholder="Enter email or phone number"
                            />
                        </Box>

                        {/* Filter Actions */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Button
                                onClick={handleClearFilters}
                                variant="outlined"
                                size="small"
                                sx={{ 
                                    color: 'primary.main',
                                    borderColor: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        borderColor: 'primary.dark',
                                    }
                                }}
                            >
                                Clear Filters
                            </Button>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button
                                    onClick={handleFilterClose}
                                    disabled={filterLoading}
                                    sx={{ 
                                        color: 'text.primary',
                                        '&:hover': {
                                            bgcolor: 'action.hover',
                                        }
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleApplyFilters}
                                    disabled={filterLoading || !hasUnsavedFilterChanges()}
                                    sx={{ 
                                        bgcolor: hasUnsavedFilterChanges() ? 'primary.main' : 'action.disabled',
                                        color: hasUnsavedFilterChanges() ? 'primary.contrastText' : 'text.disabled',
                                        '&:hover': { 
                                            bgcolor: hasUnsavedFilterChanges() ? 'primary.dark' : 'action.disabled'
                                        },
                                        minWidth: 100,
                                        position: 'relative'
                                    }}
                                >
                                    {filterLoading ? (
                                        <CircularProgress
                                            size={24}
                                            sx={{
                                                color: 'primary.contrastText',
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                marginTop: '-12px',
                                                marginLeft: '-12px',
                                            }}
                                        />
                                    ) : (
                                        'Apply'
                                    )}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Popover>

                {/* Data Display */}
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Consent Records ({totalRecords} total)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Page {page} of {totalPages} • Showing {consents.length} records
                        </Typography>
                        {hasActiveFilters() && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Active Filters: 
                                    {filters.collectionPointId && (
                                        <Chip 
                                            label={`Collection Point: ${collectionPoints.find(cp => cp.CollectionPointId === filters.collectionPointId)?.Name}`}
                                            size="small" 
                                            sx={{ ml: 1, mb: 0.5 }}
                                        />
                                    )}
                                    {filters.startDate && (
                                        <Chip 
                                            label={`From: ${new Date(filters.startDate).toLocaleDateString()}`}
                                            size="small" 
                                            sx={{ ml: 1, mb: 0.5 }}
                                        />
                                    )}
                                    {filters.endDate && (
                                        <Chip 
                                            label={`To: ${new Date(filters.endDate).toLocaleDateString()}`}
                                            size="small" 
                                            sx={{ ml: 1, mb: 0.5 }}
                                        />
                                    )}
                                    {filters.search && (
                                        <Chip 
                                            label={`Search: ${filters.search}`}
                                            size="small" 
                                            sx={{ ml: 1, mb: 0.5 }}
                                        />
                                    )}
                                    {sortBy !== 'newest' && (
                                        <Chip 
                                            label={`Sort: ${sortBy}`}
                                            size="small" 
                                            sx={{ ml: 1, mb: 0.5 }}
                                        />
                                    )}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Data Table */}
                    <TableContainer component={Paper} sx={{ boxShadow: theme.shadows[1] }}>
                        <Table sx={{ minWidth: 650 }} aria-label="sandbox consents table">
                            <TableHead>
                                <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Consent ID</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Data Principal</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Collection Point</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Consent Date</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Language</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Geolocation</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    // Loading skeleton rows
                                    Array.from({ length: pageSize }).map((_, index) => (
                                        <TableRow key={`skeleton-${index}`}>
                                            <TableCell>
                                                <Skeleton variant="text" width={80} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton variant="text" width={120} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton variant="text" width={100} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton variant="text" width={100} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton variant="text" width={60} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton variant="rectangular" width={20} height={20} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : consents.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No consent records found
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                Try adjusting your filters or search criteria
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    consents.map((consent) => (
                                        <TableRow 
                                            key={consent.ConsentHeaderId}
                                            sx={{ 
                                                '&:hover': { bgcolor: 'action.hover' },
                                                '&:last-child td, &:last-child th': { border: 0 }
                                            }}
                                        >
                                            {/* Consent ID */}
                                            <TableCell>
                                                <Button
                                                    onClick={() => handleConsentClick(consent.ConsentHeaderId)}
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        fontWeight: 'medium',
                                                        color: 'primary.main',
                                                        textTransform: 'none',
                                                        p: 0,
                                                        minWidth: 'auto',
                                                        '&:hover': {
                                                            bgcolor: 'transparent',
                                                            textDecoration: 'underline',
                                                            color: 'primary.dark'
                                                        }
                                                    }}
                                                >
                                                    #{consent.ConsentHeaderId}
                                                </Button>
                                            </TableCell>
                                            
                                            {/* Data Principal */}
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                        {consent.IdentifierValue}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {consent.IdentifierType}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            
                                            {/* Collection Point */}
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                        {consent.CollectionPointName}
                                                    </Typography>
                                                    {consent.CollectionPointDescription && (
                                                        <Tooltip title={consent.CollectionPointDescription}>
                                                            <Typography 
                                                                variant="caption" 
                                                                color="text.secondary"
                                                                sx={{ 
                                                                    display: 'block',
                                                                    maxWidth: 150,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {consent.CollectionPointDescription}
                                                            </Typography>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            
                                            {/* Consent Date */}
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                        {new Date(consent.ConsentTimestamp).toLocaleDateString()}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {new Date(consent.ConsentTimestamp).toLocaleTimeString()}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            
                                            {/* Language */}
                                            <TableCell>
                                                <Tooltip title={getLanguageTooltip(consent.LanguageCode)}>
                                                    <Chip 
                                                        label={consent.LanguageCode.toUpperCase()} 
                                                        size="small" 
                                                        sx={{ 
                                                            bgcolor: 'primary.light',
                                                            color: 'primary.contrastText',
                                                            fontWeight: 'medium'
                                                        }}
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            
                                            {/* Geolocation */}
                                            <TableCell>
                                                <Tooltip title={consent.HasGeolocation ? 'Geolocation captured' : 'No geolocation data'}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                        {consent.HasGeolocation ? (
                                                            <LocationOnIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                                        ) : (
                                                            <LocationOffIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                                                        )}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Enhanced Pagination Controls */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mt: 3,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Page {page} of {totalPages} • {totalRecords} total records
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                    value={pageSize}
                                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                    displayEmpty
                                >
                                    <MenuItem value={10}>10 per page</MenuItem>
                                    <MenuItem value={20}>20 per page</MenuItem>
                                    <MenuItem value={50}>50 per page</MenuItem>
                                    <MenuItem value={100}>100 per page</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handlePageChange(1)}
                                disabled={!hasPrevPage}
                                sx={{ minWidth: 80 }}
                            >
                                First
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handlePageChange(page - 1)}
                                disabled={!hasPrevPage}
                                sx={{ minWidth: 80 }}
                            >
                                Previous
                            </Button>
                            <Typography variant="body2" sx={{ px: 2, color: 'text.secondary' }}>
                                {page} of {totalPages}
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handlePageChange(page + 1)}
                                disabled={!hasNextPage}
                                sx={{ minWidth: 80 }}
                            >
                                Next
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={!hasNextPage}
                                sx={{ minWidth: 80 }}
                            >
                                Last
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Debug Information (remove in production) */}
                <Paper sx={{ p: 2, mt: 2, bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary">
                        <strong>Debug Info:</strong> Page {page}, PageSize {pageSize}, 
                        TotalRecords {totalRecords}, HasNext {hasNextPage.toString()}, 
                        HasPrev {hasPrevPage.toString()}
                    </Typography>
                </Paper>
            </Box>
        </Container>
    );
};

export default SandboxConsents;
